import {
  HeadingBlock,
  HeroTitle,
  Lead,
  Eyebrow,
} from "features/onboarding/guided/GuidedWelcome";
import { OnboardingStyles } from "features/onboarding/common/styles";
import GroupOverviewCard from "features/onboarding/overview/signalsGroup/GroupOverviewCard";
import React, { useContext, useEffect, useState } from "react";
import {
  OnboardingDetailedStep,
  TradingMethodType,
} from "@shared/features/onboarding/types";
import BotOverviewCard from "features/onboarding/overview/dcaBot/BotOverviewCard";
import { BotType, LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useQuery from "utils/hooks/useQuery";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import BotFormProvider from "@shared/features/bots/BotForm/common/BotFormProvider";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { CATEGORY_CONST_TO_TRADING_CATEGORY } from "features/onboarding/common/constants";
import { TradingMethodCategoryValueType } from "analytics/events";
import { selectWorkspace } from "@shared/slices/common.slice";
import { useSelector } from "react-redux";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import styled from "styled-components";
import ArtStage from "features/onboarding/art/ArtStage";
import MethodArt from "features/onboarding/art/MethodArt";
import { useOnboardingStepView } from "analytics/onboarding";

interface OverviewProps {
  tradingMethod: TradingMethodType;
}

const Overview = ({ tradingMethod }: OverviewProps) => {
  useOnboardingStepView("bot_review");
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { changeStep } = useContext(OnboardingContext)!;
  const query = useQuery();
  const botUUID = query.get("botUUID");
  const tradingMethodCategoryConst = query.get("category");
  const tradingMethodCategory: TradingMethodCategoryValueType | undefined =
    tradingMethodCategoryConst
      ? CATEGORY_CONST_TO_TRADING_CATEGORY[tradingMethodCategoryConst]
      : undefined;
  const workspace = useSelector(selectWorkspace);
  const [isCreated, setIsCreated] = useState(false);

  useEffect(() => {
    if (tradingMethod === TradingMethodType.DCA_BOT && botUUID === null)
      changeStep(OnboardingDetailedStep.COPY_DCA_BOT);
  }, [botUUID]);

  const TRADING_METHOD_TO_OVERVIEW_CARD = {
    [TradingMethodType.DCA_BOT]: (
      <BotFormProvider
        botFormType={BotFormType.COPY_PUBLIC}
        botType={BotType.ASAP}
        botUUID={botUUID || ""}
        isDemoCopyPublic={workspace === WorkspaceType.DEMO}
      >
        <BotOverviewCard
          tradingMethodCategory={tradingMethodCategory}
          onCreated={() => setIsCreated(true)}
        />
      </BotFormProvider>
    ),
    [TradingMethodType.SIGNALS_GROUP]: (
      <GroupOverviewCard
        tradingMethodCategory={tradingMethodCategory}
        onCreated={() => setIsCreated(true)}
      />
    ),
  };

  return (
    <OnboardingStyles.StepWrapper
      style={
        isCreated
          ? { maxWidth: "650px" }
          : { maxWidth: "1120px", width: "100%" }
      }
    >
      {!isCreated && (
        <HeadingBlock>
          <Eyebrow>
            {t(
              tradingMethod === TradingMethodType.DCA_BOT
                ? "intent.dca.name"
                : "intent.signals.name"
            )}{" "}
            ·{" "}
            {t(
              workspace === WorkspaceType.DEMO
                ? "guided.virtual_only"
                : "intent.live_setup"
            )}
          </Eyebrow>
          <HeroTitle>{t("intent.review_title")}</HeroTitle>
          <Lead>{t("intent.review_description")}</Lead>
        </HeadingBlock>
      )}
      <ReviewLayout $aside={!isCreated}>
        <div>{TRADING_METHOD_TO_OVERVIEW_CARD[tradingMethod]}</div>
        {!isCreated && (
          <ReviewAside>
            <ArtStage>
              <MethodArt
                method={
                  tradingMethod === TradingMethodType.DCA_BOT
                    ? "dca"
                    : "signals"
                }
              />
            </ArtStage>
          </ReviewAside>
        )}
      </ReviewLayout>
    </OnboardingStyles.StepWrapper>
  );
};

export default Overview;

const ReviewLayout = styled.div<{ $aside: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${(p) =>
    p.$aside ? "minmax(0, 560px) minmax(0, 1fr)" : "minmax(0, 1fr)"};
  gap: 24px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const ReviewAside = styled.aside`
  position: sticky;
  top: 16px;
  @media (max-width: 1100px) {
    display: none;
  }
`;
